<template>
  <transition-group
    name="chat-toast"
    tag="div"
    class="chat-message-toast-stack"
  >
    <div
      v-for="item in toasts"
      :key="item.id"
      class="chat-message-toast-root"
      @click="onClick(item)"
    >
      <div
        class="chat-message-toast"
        :style="{ '--toast-duration': toastDurationMs + 'ms' }"
      >
        <div class="chat-message-toast-avatar">
          <v-icon size="16" color="#3B82F6">mdi-message-text-outline</v-icon>
        </div>
        <div class="chat-message-toast-body">
          <div class="chat-message-toast-title">{{ $t("conversations") }}</div>
          <div class="chat-message-toast-preview">
            {{ $t("conversation-new-message") }}
          </div>
        </div>
        <div class="chat-message-toast-progress" />
      </div>
    </div>
  </transition-group>
</template>

<script>
import { normalizeChatEventName } from "@/realtime/chat";

export default {
  name: "ChatMessageToast",
  data() {
    return {
      toasts: [],
      toastSeq: 0,
      pendingChatId: null,
      pendingMessageId: null,
      activeChatId: null,
      lastCount: null,
      baselineReady: false,
      baselineFallbackTimer: null,
      // One-shot gate: live event arms toast; badge increase fires it once.
      expectToast: false,
      expectToastTimer: null,
      maxToasts: 5,
      toastDurationMs: 6000,
    };
  },
  computed: {
    chatUnreadCount() {
      return Number(this.$store.state.chatUnreadCount) || 0;
    },
  },
  watch: {
    chatUnreadCount: {
      immediate: true,
      handler(next) {
        var current = Number(next) || 0;
        if (!this.baselineReady) {
          this.lastCount = current;
          return;
        }
        var previous = Number(this.lastCount) || 0;
        this.lastCount = current;
        if (current <= previous) {
          return;
        }
        // Sync/reconcile can raise the number too; only toast if armed by live event.
        if (!this.expectToast) {
          return;
        }
        this.expectToast = false;
        if (this.expectToastTimer) {
          clearTimeout(this.expectToastTimer);
          this.expectToastTimer = null;
        }
        // Exactly one toast per armed live event (never spam old unread diff).
        this.pushToast();
      },
    },
  },
  created() {
    if (this.$root) {
      this.$root.$on("chat-live-event", this.onChatLiveEvent);
      this.$root.$on("chat-active-changed", this.onChatActiveChanged);
      this.$root.$on(
        "chat-unread-baseline-ready",
        this.onUnreadBaselineReady
      );
    }
  },
  mounted() {
    var self = this;
    this.baselineFallbackTimer = setTimeout(function () {
      self.onUnreadBaselineReady();
    }, 4000);
  },
  beforeDestroy() {
    if (this.$root) {
      this.$root.$off("chat-live-event", this.onChatLiveEvent);
      this.$root.$off("chat-active-changed", this.onChatActiveChanged);
      this.$root.$off(
        "chat-unread-baseline-ready",
        this.onUnreadBaselineReady
      );
    }
    if (this.baselineFallbackTimer) {
      clearTimeout(this.baselineFallbackTimer);
      this.baselineFallbackTimer = null;
    }
    if (this.expectToastTimer) {
      clearTimeout(this.expectToastTimer);
      this.expectToastTimer = null;
    }
    this.clearAllTimers();
  },
  methods: {
    onUnreadBaselineReady() {
      if (this.baselineReady) {
        return;
      }
      if (this.baselineFallbackTimer) {
        clearTimeout(this.baselineFallbackTimer);
        this.baselineFallbackTimer = null;
      }
      this.lastCount = Number(this.chatUnreadCount) || 0;
      this.baselineReady = true;
      this.expectToast = false;
    },
    onChatActiveChanged(chatId) {
      this.activeChatId =
        chatId != null && chatId !== "" ? chatId : null;
    },
    armExpectToast() {
      var self = this;
      this.expectToast = true;
      if (this.expectToastTimer) {
        clearTimeout(this.expectToastTimer);
      }
      // Auto-disarm so later drawer sync cannot replay old unread as toasts.
      this.expectToastTimer = setTimeout(function () {
        self.expectToast = false;
        self.expectToastTimer = null;
      }, 4000);
    },
    onChatLiveEvent(payload) {
      if (!this.baselineReady) {
        return;
      }
      var action = String(normalizeChatEventName(payload) || "")
        .replace(/^\./, "")
        .toLowerCase();
      if (!this.isIncomingMessageSignal(action)) {
        return;
      }

      var data = this.extractData(payload);
      var message = data.message || null;
      if (message && this.isMine(message, data)) {
        return;
      }
      var chatId = this.resolveChatId(data);
      if (chatId == null) {
        return;
      }
      if (
        this.activeChatId != null &&
        Number(this.activeChatId) === Number(chatId)
      ) {
        return;
      }

      this.pendingChatId = chatId;
      this.pendingMessageId =
        message && message.id != null
          ? message.id
          : data.message_id != null
            ? data.message_id
            : null;
      this.armExpectToast();
    },
    isIncomingMessageSignal(action) {
      if (!action) {
        return false;
      }
      return (
        action.indexOf("message.created") !== -1 ||
        action.indexOf("chat.badge") !== -1 ||
        action === "badge" ||
        action.indexOf("chat.updated") !== -1
      );
    },
    pushToast() {
      if (
        this.activeChatId != null &&
        this.pendingChatId != null &&
        Number(this.activeChatId) === Number(this.pendingChatId)
      ) {
        return;
      }

      while (this.toasts.length >= this.maxToasts) {
        this.removeToast(this.toasts[0].id);
      }

      var id = ++this.toastSeq;
      var item = {
        id: id,
        chatId: this.pendingChatId,
        messageId: this.pendingMessageId,
        timer: null,
      };
      var self = this;
      item.timer = setTimeout(function () {
        self.removeToast(id);
      }, this.toastDurationMs);

      this.toasts.push(item);
    },
    removeToast(id) {
      var index = this.toasts.findIndex(function (item) {
        return item.id === id;
      });
      if (index === -1) {
        return;
      }
      var item = this.toasts[index];
      if (item.timer) {
        clearTimeout(item.timer);
      }
      this.toasts.splice(index, 1);
    },
    clearAllTimers() {
      this.toasts.forEach(function (item) {
        if (item.timer) {
          clearTimeout(item.timer);
        }
      });
      this.toasts = [];
    },
    onClick(item) {
      // Opening drawer sync must never create more toasts.
      this.expectToast = false;
      if (this.expectToastTimer) {
        clearTimeout(this.expectToastTimer);
        this.expectToastTimer = null;
      }
      this.removeToast(item.id);
      this.$root.$emit(
        "open-user-conversation",
        item.chatId != null
          ? { chatId: item.chatId, messageId: item.messageId }
          : null
      );
    },
    resolveChatId(data) {
      if (!data) {
        return null;
      }
      if (data.chat_id != null) {
        return data.chat_id;
      }
      if (data.chatId != null) {
        return data.chatId;
      }
      if (data.chat && data.chat.id != null) {
        return data.chat.id;
      }
      return null;
    },
    extractData(payload) {
      var root = payload && payload.data != null ? payload.data : payload;
      if (typeof root === "string") {
        try {
          root = JSON.parse(root);
        } catch (e) {
          return {};
        }
      }
      if (!root || typeof root !== "object") {
        return {};
      }
      if (
        root.data &&
        typeof root.data === "object" &&
        !Array.isArray(root.data) &&
        (root.data.chat_id != null ||
          root.data.action ||
          root.data.message ||
          root.data.chat ||
          root.data.badges)
      ) {
        return Object.assign({}, root, root.data);
      }
      return root;
    },
    isMine(message, data) {
      var candidates = [
        data && data.sender_id,
        message && message.user_id,
        message && message.user && message.user.id,
        message && message.user && message.user.uid,
      ];
      for (var i = 0; i < candidates.length; i++) {
        if (this.isCurrentUserIdentity(candidates[i])) {
          return true;
        }
      }
      return false;
    },
    isCurrentUserIdentity(value) {
      if (value == null || value === "") {
        return false;
      }
      var storeUserId =
        this.$store && this.$store.state ? this.$store.state.userId : null;
      if (this.isSameUserId(value, storeUserId)) {
        return true;
      }
      var cached = null;
      try {
        cached = sessionStorage.getItem("conversation-my-user-id");
      } catch (e) {}
      return this.isSameUserId(value, cached);
    },
    isSameUserId(a, b) {
      if (a == null || b == null || a === "" || b === "") {
        return false;
      }
      if (String(a) === String(b)) {
        return true;
      }
      var na = Number(a);
      var nb = Number(b);
      return !isNaN(na) && !isNaN(nb) && na === nb;
    },
  },
};
</script>

<style>
.chat-message-toast-stack {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 10050;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: calc(100vw - 32px);
  width: 280px;
  pointer-events: none;
  direction: rtl;
}
.chat-message-toast-root {
  width: 100%;
  cursor: pointer;
  pointer-events: auto;
}
.chat-message-toast {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px 10px;
  border-radius: 10px;
  background: #eef5ff;
  border: 1px solid #cfe0f8;
  color: #334155;
  text-align: right;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.1);
  transition:
    background 0.18s ease,
    box-shadow 0.18s ease;
}
.chat-message-toast-root:hover .chat-message-toast {
  background: #e5f0ff;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.14);
}
.chat-message-toast-avatar {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chat-message-toast-body {
  min-width: 0;
  flex: 1 1 auto;
}
.chat-message-toast-title {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 1px;
  color: #1e3a5f;
}
.chat-message-toast-preview {
  font-size: 11px;
  line-height: 1.3;
  color: #5b7aa0;
}
.chat-message-toast-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(59, 130, 246, 0.18);
  overflow: hidden;
}
.chat-message-toast-progress::after {
  content: "";
  display: block;
  height: 100%;
  width: 100%;
  background: #3b82f6;
  transform-origin: right center;
  animation: chat-toast-progress linear forwards;
  animation-duration: var(--toast-duration, 6000ms);
}
@keyframes chat-toast-progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

.chat-toast-enter-active,
.chat-toast-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}
.chat-toast-move {
  transition: transform 0.26s cubic-bezier(0.22, 1, 0.36, 1);
}
.chat-toast-enter {
  opacity: 0;
  transform: translateY(10px);
}
.chat-toast-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.chat-toast-leave-active {
  position: absolute;
  width: 280px;
  max-width: calc(100vw - 32px);
}
.chat-toast-leave {
  opacity: 1;
  transform: translateY(0);
}
.chat-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
